<template>
  <div class="cover-limit-reached-notice" :class="productCode">
    <div class="message-container">
      <v-icon :color="color">{{ icons.mdiAlertCircleOutline }}</v-icon>
      <p :class="productCode">
        Your requirements exceed our current cover limit. We recommend speaking
        with an adviser who can help tailor a solution that meets your specific
        needs.
        <a v-if="showExceptionBannerLink" @click="bannerLimitClicked"
          >I want to speak to an advisor</a
        >
      </p>
    </div>
    <v-dialog
      class="request-a-call-modal"
      v-model="requestACallSuccessModal"
      max-width="440px"
      rounded="0"
    >
      <v-card rounded="0">
        <div class="request-a-call-modal-content">
          <div class="title">
            <div class="left">
              <h1>Thank you</h1>
            </div>
            <div class="right">
              <v-img
                @click="requestACallSuccessModal = false"
                aspect-ratio="1"
                :src="require('@/assets/shared/insurance/close_vector.svg')"
              />
            </div>
          </div>
          <div class="description">
            <p>An advisor will contact you.</p>
          </div>
          <div class="button-section">
            <GradientButton
              @gradientButtonClicked="downloadWillClicked"
              :title="`DOWNLOAD MY WILL`"
            />
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import { mdiAlertCircleOutline } from '@mdi/js';
  import {
    INSURANCE_EXCEPTION_MUTATION,
    TESTATOR_QUERY,
  } from '../../constants/graphql/testators';
  export default {
    name: 'CoverLimitReachedNotice',
    props: {
      productCode: {
        required: true,
        type: String,
      },
      testator: {
        required: true,
        type: Object,
      },
    },
    computed: {
      color() {
        switch (this.productCode) {
          case 'COC':
            return '#e9ab21';
          case 'CAC':
            return '#8aba3e';
          case 'DAC':
            return '#d65e23';
          case 'EC_D':
            return '#7395ef';
          case 'TAC':
            return '#e9ab21';
          default:
            break;
        }

        return 'red';
      },
      showExceptionBannerLink() {
        return (
          !this.testator.insuranceException?.exceptionReasons?.includes(
            'cover_limit_reached'
          ) ?? false
        );
      },
    },
    methods: {
      bannerLimitClicked() {
        this.$apollo
          .mutate({
            mutation: INSURANCE_EXCEPTION_MUTATION,
            variables: {
              testatorId: this.testatorId,
            },
            update: (cache, { data: { insuranceException } }) => {
              this.updateCacheAfterCreateInsuranceException(
                cache,
                insuranceException
              );
            },
          })
          .then(response => {
            response.data.insuranceException
              ? this.showSuccessMessage()
              : this.displayError();
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      updateCacheAfterCreateInsuranceException(cache, insuranceException) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        data.testator.insuranceException = insuranceException;

        cache.writeQuery({ ...query, data: data });
      },
      showSuccessMessage() {
        this.requestACallSuccessModal = true;
      },
      displayError() {},
      downloadWillClicked() {
        this.skip();
      },
      skip() {
        const insuranceInputDetails = {
          testatorId: this.testator.id,
          skipInsurance: true,
        };
        const params = {
          insuranceInputDetails: insuranceInputDetails,
          redirectLocation: 'OverviewPage',
        };
        this.$emit('updateInsurance', params);
      },
    },

    data(props) {
      return {
        requestACallSuccessModal: false,
        icons: {
          mdiAlertCircleOutline: mdiAlertCircleOutline,
        },
      };
    },
  };
</script>

<style lang="scss" scoped>
  .request-a-call-modal-content {
    padding: 30px;

    .title {
      display: flex;
      justify-content: space-between;
      .left {
        h1 {
          font-size: 22px;
          font-weight: 600;
          line-height: 26.82px;
          color: #000000;
          font-family: 'Montserrat';
        }
      }
      .right {
        .v-image {
          cursor: pointer;
        }
      }
    }
    .description {
      margin-top: 16px;
      p {
        margin: 0;
        font-size: 16px;
        font-weight: 400;
        line-height: 22px;
        color: #282828;
      }
    }
    .button-section {
      margin-top: 32px;
    }
  }
  .cover-limit-reached-notice {
    margin-bottom: 26px;
    padding: 16px;
    border-radius: 8px;

    .message-container {
      display: flex;
      gap: 8px;
      align-items: start;

      p {
        margin: 0;
        font-size: 12px;
        font-weight: 600;
        line-height: 20px;
        text-align: left;
        a {
          margin: 0;
          font-size: 12px;
          font-weight: 600;
          line-height: 20px;
          text-align: left;
          text-decoration: underline;
        }
      }

      p.COC {
        color: #e9ab21;
      }
      p.CAC {
        color: #8aba3e;
      }
      p.DAC {
        color: #d65e23;
      }
      p.EC_D {
        color: #7395ef;
      }
      p.TAC {
        color: #e9ab21;
      }

      p.COC a {
        color: #e9ab21;
      }
      p.CAC a {
        color: #8aba3e;
      }
      p.DAC a {
        color: #d65e23;
      }
      p.EC_D a {
        color: #7395ef;
      }
      p.TAC a {
        color: #e9ab21;
      }
    }
  }

  .cover-limit-reached-notice.COC {
    border: 1px solid #e9ab21;
    background: #d65e231a;
  }
  .cover-limit-reached-notice.CAC {
    border: 1px solid #8aba3e;
    background: #8aba3e1a;
  }
  .cover-limit-reached-notice.DAC {
    border: 1px solid #d65e23;
    background: #d65e231a;
  }
  .cover-limit-reached-notice.EC_D {
    border: 1px solid #7395ef;
    background: #7395ef1a;
  }
  .cover-limit-reached-notice.TAC {
    border: 1px solid #e9ab21;
    background: #e9ab211a;
  }
</style>
